/**
 * 管理后台的小构件（卡片 / 键值表 / 命令清单）。
 *
 * 单独放一份的原因：管理页里"一个数字一张卡"的排版重复度很高，
 * 复制三遍就会出现三种间距与字号，看起来像拼凑的页面。
 */
import type { ReactNode } from 'react';
import { cn } from '../../lib/utils';

export function StatCard({
  label,
  value,
  hint,
  tone = 'neutral',
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  tone?: 'neutral' | 'brand' | 'warn' | 'danger';
}) {
  const tones: Record<string, string> = {
    neutral: 'border-slate-200 bg-white',
    brand: 'border-brand-200 bg-brand-50/60',
    warn: 'border-amber-200 bg-amber-50/60',
    danger: 'border-rose-200 bg-rose-50/60',
  };
  return (
    <div className={cn('rounded-2xl border p-4 shadow-sm', tones[tone])}>
      <p className="text-xs font-medium text-slate-500">{label}</p>
      <p className="mt-1 text-2xl font-bold text-slate-900">{value}</p>
      {hint && <p className="mt-0.5 text-xs text-slate-500">{hint}</p>}
    </div>
  );
}

/** 只读的键值列表：看板上的主机/进程信息用它，避免每行都写一遍布局。 */
export function KeyValueList({
  items,
  columns = 2,
}: {
  items: Array<{ label: string; value: ReactNode }>;
  columns?: 1 | 2 | 3;
}) {
  const cols: Record<1 | 2 | 3, string> = {
    1: 'sm:grid-cols-1',
    2: 'sm:grid-cols-2',
    3: 'sm:grid-cols-3',
  };
  return (
    <dl className={cn('grid grid-cols-1 gap-x-6 gap-y-2', cols[columns])}>
      {items.map((item) => (
        <div key={item.label} className="flex items-baseline justify-between gap-3 border-b border-dashed border-slate-200 py-1.5">
          <dt className="shrink-0 text-xs text-slate-500">{item.label}</dt>
          <dd className="min-w-0 break-all text-right text-sm font-medium text-slate-800">
            {item.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * 只读诊断的"命令清单"。
 * 方案 2B.3 的替代做法：管理面板不提供危险按钮，只给可以在服务器上
 * 复制粘贴的命令 —— 多一步粘贴，但出错时不会把系统搞死。
 */
export function ManualCommands({ commands, title = '在服务器上执行' }: { commands: string[]; title?: string }) {
  return (
    <div className="mt-3 rounded-xl bg-slate-900 p-3">
      <p className="mb-2 text-xs font-medium text-slate-300">{title}（网页不提供按钮，避免误操作）</p>
      <pre className="overflow-x-auto whitespace-pre-wrap break-all text-[11px] leading-relaxed text-emerald-300">
        {commands.join('\n')}
      </pre>
    </div>
  );
}

/** 字节数转人类可读；管理页里到处都要显示大小。 */
export function formatBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined) return '-';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(unit === 0 ? 0 : 1)} ${units[unit] ?? 'B'}`;
}

/**
 * 秒 → `'5 天 3 小时'` —— **部署实例自己的运行时长**（主机 / 进程 uptime）。
 *
 * ⚠️ 它**不是**"用时"，所以刻意**不**与 `@fht/shared` 的 `formatDuration()`
 * （实际用时：`'45 秒'` / `'19 分 23 秒'`）合并，两点区别：
 *   1. 语义不同：uptime 是进程活了多久（系统指标），"用时"是孩子投入了多久；
 *   2. 口径不同：运维看的是"这台机器活了多少天"，天数不能被拆成 120 小时，
 *      秒级精度也没有意义。而用时必须保留秒（见 shared/duration.ts）。
 *
 * 改名的原因：它以前也叫 `formatDuration`，与 shared 那个**同名不同口径** ——
 * 一个页面 import 哪个就能得到两种完全不同的文案，正是这个项目反复踩的
 * "同一概念多处实现"的坑。名字里带 `Uptime` 之后，两者不会再被混用。
 */
export function formatUptime(seconds: number): string {
  const days = Math.floor(seconds / 86_400);
  const hours = Math.floor((seconds % 86_400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (days > 0) return `${days} 天 ${hours} 小时`;
  if (hours > 0) return `${hours} 小时 ${minutes} 分`;
  return `${minutes} 分`;
}
